










import { CreateElement, VNode } from 'vue';
import { Component, Prop, Model, Provide, Watch } from 'vue-property-decorator';
import { SwExtendedVue } from '../sw-extended-vue';
import SwTabNav from './sw-tab-nav.vue';
import SwTabPage from './sw-tab-page.vue';
import { TabProps, ProvidedTabParam } from './types';
import { utilService } from '../util-service';

const cssPrefix = 'sw-tab-group';

/**
 * Events:
 * @event select `(tabId: string)`
 * @event close `(tabId: string)`
 */
@Component({
  components: {
    SwTabNav,
    SwTabPage,
  },
})
export default class SwTabGroup extends SwExtendedVue {
  @Model('select', { type: String, required: true })
  value: string;

  @Prop({
    type: Array,
    default() {
      return [];
    },
  })
  tabsProps: TabProps[];

  /** tab level, for now only can it be 1 or 2 */
  @Prop({ type: Number, default: 1 })
  level: number;

  /** vertical tab will invalidate tab level */
  @Prop({ type: Boolean, default: false })
  vertical: boolean;

  @Prop(Number)
  tabGutter: number;

  /** show an arrow indicator under selected tab. Only effective if `level === 1` and `vertical === false` */
  @Prop({ type: Boolean, default: false })
  arrowIndicator: boolean;

  @Prop({ type: String, default: 'light' })
  theme: 'light' | 'dark';

  @Prop({ type: Boolean, default: false })
  centered: boolean;

  @Prop({ type: Boolean, default: false })
  withScorllbar: boolean;

  cssPrefix = cssPrefix;

  @Provide()
  tabParam: ProvidedTabParam = {
    level: this.effectiveLevel,
    vertical: this.vertical,
    tabGutter: this.tabGutter,
    arrowIndicator: this.arrowIndicator,
    theme: this.theme,
    centered: this.centered,
    withScorllbar: this.withScorllbar,
  };

  get effectiveLevel(): number {
    return this.vertical ? 1 : this.level;
  }

  @Provide()
  private claimTabSelected(tabId: string) {
    this.$emit('select', tabId);
  }

  @Provide()
  private claimTabClose(tabId: string) {
    this.$emit('close', tabId);
  }

  @Watch('effectiveLevel')
  @Watch('vertical')
  @Watch('tabGutter')
  @Watch('arrowIndicator')
  @Watch('theme')
  @Watch('centered')
  @Watch('withScorllbar')
  private syncParameters() {
    Object.assign<ProvidedTabParam, ProvidedTabParam>(
      this.tabParam,
      {
        level: this.effectiveLevel,
        vertical: this.vertical,
        tabGutter: this.tabGutter,
        arrowIndicator: this.arrowIndicator,
        theme: this.theme,
        centered: this.centered,
        withScorllbar: this.withScorllbar,
      },
    );
  }

  private hasExtra() {
    const slot = utilService.getVueScopedSlot(this.$scopedSlots, 'extra');
    return slot.length > 0;
  }

  private pickPageNode() {
    const slot = utilService.getVueScopedSlot(this.$scopedSlots);
    if (slot.length <= 0) { return null; }
    return slot.find((node) => {
      if (!node.componentOptions || node.componentOptions.tag !== 'sw-tab-page') { return false; }
      if (!node.componentOptions.propsData ||
        (node.componentOptions.propsData as TabProps).tabId !== this.value) { return false; }
      return true;
    });
  }
  /* tslint:disable-next-line */
  render(h: CreateElement): VNode {
    let barExtraNode = null;

    if (this.hasExtra()) {
      barExtraNode = (
        <div
          class={[
            `${this.cssPrefix}__bar__extra`,
            'sw-flexbox__flex-none', 'sw-flexbox', 'sw-flexbox--center-items',
          ]}
        >
          { utilService.getVueScopedSlot(this.$scopedSlots, 'extra') }
        </div>
      );
    }

    const rootNode = (
      <div
        class={[
          `${this.cssPrefix}`,
          `${this.cssPrefix}--${this.theme}`,
          { 'sw-flexbox': this.vertical },
          'sw-typo-default',
        ]}
      >
        <div
          class={[
            `${this.cssPrefix}__bar`,
            'sw-flexbox',
            {
              'sw-flexbox--column': this.vertical,
              'sw-flexbox__flex-none': this.vertical,
            },
          ]}
        >
          <sw-tab-nav
            class={['sw-flexbox__flex']}
            tabs={this.tabsProps}
            activeTabId={this.value}
          ></sw-tab-nav>
          {barExtraNode}
        </div>
        <div
          class={[
            `${this.cssPrefix}__pages`,
            { 'sw-flexbox__flex': this.vertical },
          ]}
        >
          {this.pickPageNode()}
        </div>
      </div>
    );
    return rootNode as VNode;
  }
}
